(() => {
  const setup = (scope = document) => {
    scope.querySelectorAll('[data-video-hero]').forEach(hero => {
      if (hero.dataset.ready) return;
      hero.dataset.ready = 'true';
      const controls = hero.querySelector('[data-version-controls]');
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      let version = hero.dataset.heroVersion || 'reveal';
      let onScreen = true;
      const players = Array.from(hero.querySelectorAll('[data-version-panel]')).flatMap(panel => {
        const video = panel.querySelector('video');
        if (!video) return [];
        const button = panel.querySelector('[data-video-play]');
        const status = panel.querySelector('[data-video-status]');
        const player = { panel, video, button, wantsMotion: !reduced.matches };
        const update = () => {
          if (!button) return;
          button.textContent = video.paused ? 'Play animation' : 'Pause animation';
          button.setAttribute('aria-pressed', String(!video.paused));
        };
        player.update = update;
        button?.addEventListener('click', () => { player.wantsMotion = video.paused; sync(); });
        video.addEventListener('play', update);
        video.addEventListener('pause', update);
        video.addEventListener('error', () => {
          if (status) status.textContent = 'Showing our chocolate photograph while the animation is unavailable.';
          update();
        });
        return [player];
      });
      const reveals = Array.from(hero.querySelectorAll('[data-version-panel]')).filter(panel => panel.querySelector('[data-reveal-scene]')).map(panel => {
        const button = panel.querySelector('[data-reveal-play]');
        const pieces = Array.from(panel.querySelectorAll('[data-reveal-piece]'));
        const rightToLeft = panel.dataset.versionPanel === 'rows-rtl';
        const byRow = panel.dataset.versionPanel === 'rows' || rightToLeft;
        const ordered = byRow ? [...pieces].sort((a, b) => Number(a.dataset.row) - Number(b.dataset.row) || (rightToLeft ? Number(b.dataset.x) - Number(a.dataset.x) : Number(a.dataset.x) - Number(b.dataset.x))) : pieces;
        const crumbs = Array.from(panel.querySelectorAll('[data-reveal-crumbs]'));
        const rowIndex = piece => byRow ? Number(piece.dataset.row) : 0;
        const enterTime = piece => ordered.indexOf(piece) * (rightToLeft ? 300 : 220) + rowIndex(piece) * 180 + 1;
        const holdStart = Math.max(...pieces.map(enterTime)) + 220;
        const leaveTime = piece => holdStart + 1800 + ordered.indexOf(piece) * 120 + rowIndex(piece) * 100;
        const duration = Math.max(...pieces.map(leaveTime)) + 900;
        const state = { panel, button, wantsMotion: !reduced.matches, holdTime: holdStart + 500 };
        const crumbAnimations = [];
        state.animations = pieces.map((piece, index) => {
          const enter = enterTime(piece) / duration;
          const leave = leaveTime(piece) / duration;
          // One by One treats every brownie alike; row variants keep their anchor.
          const isAnchor = panel.dataset.versionPanel !== 'reveal' && (rightToLeft ? piece === ordered[0] : index === 0);
          const frames = isAnchor ? [
            { opacity: 1, offset: 0, easing: 'steps(1, end)' },
            { opacity: 0, offset: leave, easing: 'steps(1, end)' },
            { opacity: 1, offset: leave + 320 / duration },
            { opacity: 1, offset: 1 }
          ] : [
            { opacity: 0, offset: 0, easing: 'steps(1, end)' },
            { opacity: 1, offset: enter, easing: 'steps(1, end)' },
            { opacity: 0, offset: leave },
            { opacity: 0, offset: 1 }
          ];
          const animation = piece.animate(frames, { duration, iterations: Infinity });
          animation.pause();
          animation.currentTime = reduced.matches ? state.holdTime : 0;
          if (crumbs[index]) {
            // Crumbs begin only after this brownie first disappears, then remain
            // until its next entrance. Each group has its own photographed scatter.
            const crumbEnd = isAnchor ? 320 / duration : 1 - leave + enter;
            const crumbAnimation = crumbs[index].animate([
              { opacity: 1, offset: 0, easing: 'steps(1, end)' },
              { opacity: 0, offset: crumbEnd, easing: 'steps(1, end)' },
              { opacity: 0, offset: 1 }
            ], { duration, delay: leaveTime(piece), iterations: Infinity });
            crumbAnimation.pause();
            crumbAnimation.currentTime = animation.currentTime;
            crumbAnimations.push(crumbAnimation);
          }
          return animation;
        });
        state.animations.push(...crumbAnimations);
        button?.addEventListener('click', () => { state.wantsMotion = !state.wantsMotion; sync(); });
        return state;
      });
      const syncReveal = () => reveals.forEach(state => {
        const playing = version === state.panel.dataset.versionPanel && state.wantsMotion && onScreen && !document.hidden;
        state.animations.forEach(animation => playing ? animation.play() : animation.pause());
        if (state.button) {
          state.button.textContent = playing ? 'Pause animation' : 'Play animation';
          state.button.setAttribute('aria-pressed', String(playing));
        }
      });
      const sync = () => {
        syncReveal();
        players.forEach(player => {
          const { panel, video, update } = player;
          if (panel.dataset.versionPanel !== version || !player.wantsMotion || !onScreen || document.hidden) {
            video.pause();
            update();
            return;
          }
          const source = video.querySelector('source');
          if (!source.hasAttribute('src')) {
            source.src = source.dataset.src;
            video.load();
          }
          video.play().catch(update);
        });
      };
      const select = choice => {
        if (version !== choice && !reduced.matches) reveals.filter(state => state.panel.dataset.versionPanel === choice).forEach(state => state.animations.forEach(animation => { animation.currentTime = 0; }));
        version = choice;
        hero.querySelectorAll('[data-version-panel]').forEach(panel => {
          panel.hidden = panel.dataset.versionPanel !== choice;
        });
        hero.querySelectorAll('[data-version]').forEach(button => {
          button.setAttribute('aria-pressed', String(button.dataset.version === choice));
        });
        sync();
      };
      const tilt = hero.querySelector('[data-hero-tilt]');
      const tiltTo = (x = 0, y = 0) => {
        if (!tilt || reduced.matches) return;
        tilt.style.setProperty('--tilt-x', `${-y * 4}deg`);
        tilt.style.setProperty('--tilt-y', `${x * 5}deg`);
        tilt.style.setProperty('--tilt-z', `${x * 1.5}deg`);
      };
      tilt?.addEventListener('pointermove', event => {
        if (event.pointerType !== 'mouse') return;
        const bounds = tilt.getBoundingClientRect();
        tiltTo((event.clientX - bounds.left) / bounds.width * 2 - 1, (event.clientY - bounds.top) / bounds.height * 2 - 1);
      });
      tilt?.addEventListener('pointerleave', () => tiltTo());
      tilt?.addEventListener('blur', () => tiltTo());
      tilt?.addEventListener('keydown', event => {
        const direction = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1], Escape: [0, 0] }[event.key];
        if (direction) { event.preventDefault(); tiltTo(...direction); }
      });
      if (controls) controls.hidden = false;
      select(version);
      controls?.addEventListener('click', event => {
        const button = event.target.closest('[data-version]');
        if (button && controls.contains(button)) select(button.dataset.version);
      });
      document.addEventListener('visibilitychange', sync);
      const observer = new IntersectionObserver(entries => {
        onScreen = entries[0].isIntersecting;
        sync();
      }, { threshold: 0.05 });
      observer.observe(hero);
      const onPreference = event => {
        reveals.forEach(state => {
          state.wantsMotion = !event.matches;
          if (event.matches) state.animations.forEach(animation => { animation.currentTime = state.holdTime; });
        });
        players.forEach(player => { player.wantsMotion = !event.matches; });
        sync();
      };
      reduced.addEventListener('change', onPreference);
      const cleanup = event => {
        if (!event.target.contains(hero)) return;
        observer.disconnect();
        reveals.forEach(state => state.animations.forEach(animation => animation.cancel()));
        players.forEach(({ video }) => video.pause());
        reduced.removeEventListener('change', onPreference);
        document.removeEventListener('visibilitychange', sync);
        document.removeEventListener('shopify:section:unload', cleanup);
      };
      document.addEventListener('shopify:section:unload', cleanup);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setup());
  else setup();
  document.addEventListener('shopify:section:load', event => setup(event.target));
})();
